import React from 'react';
import { Toggle } from '@dropbox/dig-components/controls';
import { Box } from '@dropbox/dig-foundations';
import { FormLabel } from '@dropbox/dig-components/form_row';
import { defineMessages, useIntl } from 'react-intl';
import { atom, useAtom } from 'jotai';
import { Text } from '@dropbox/dig-components/typography';
import { useBreakpoint } from '@dropbox/dig-components/hooks';
import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { ChevronLeftLine } from '@dropbox/dig-icons/assets';
import { logPAPEvent } from '../../js/sign-components/common/product-analytics-pipeline';
import { PAP_Click_FormViewToggle } from '@dropbox/pap-events/sign/click_form_view_toggle';
import { useSignerAppParams } from '../signer-experience/signer-experience';
import { useBundleProps } from '../bundle-props';
import { useFeatureFlag } from '../context/feature-flags';
import { useMatchMedia } from '../utils/use-match-media';
import styles from './accessible-document-toggle.module.css';

export const accessibleViewAtom = atom(false);

const messages = defineMessages({
  mobile: {
    id: 'e5ea2ac616dd4c124c236fa8e26d4b637d8410d9762af55c25085642988c547d',
    defaultMessage: 'Form view',
    description: 'Label for mobile view toggle',
  },
  openAsForm: {
    id: 'a1e862396321ce937b1cb2050c0dbb25bd2a6d01c1a5017fb98c4a0750488a66',
    defaultMessage: 'Open as form',
    description: 'Button to switch from the document to form view.',
  },
  backToDocument: {
    id: '44d948fdc798f0106d804a1fa599fd02bd2c8f08ef4e96728799b5515a4abdf6',
    defaultMessage: 'Back to document',
    description: 'Button to switch from form view back to the document.',
  },
});

export function AccessibleDocumentToggle({ disable = false }) {
  const intl = useIntl();
  const [isAccessibleView, setAccessibleView] = useAtom(accessibleViewAtom);
  const { signatureRequestId } = useSignerAppParams();
  const { hasCustomStyles } = useBundleProps();
  const { small: displaySmall } = useBreakpoint();
  const shouldUseIconButton = useMatchMedia('(max-width: 400px)');
  const formViewUiImprovements = useFeatureFlag(
    'sign_core_2026_07_03_form_view_ui_improvements',
  );

  const toggleView = React.useCallback(() => {
    const isFormView = !isAccessibleView;
    logPAPEvent(
      PAP_Click_FormViewToggle({
        enableFormView: isFormView,
        signatureRequestId,
      }),
    );
    setAccessibleView(isFormView);
  }, [isAccessibleView, setAccessibleView, signatureRequestId]);

  if (formViewUiImprovements) {
    const label = intl.formatMessage(
      isAccessibleView ? messages.backToDocument : messages.openAsForm,
    );
    const icon = isAccessibleView ? (
      <UIIcon src={ChevronLeftLine} />
    ) : (
      <span className={styles.openFormViewIcon} aria-hidden="true" />
    );

    if (shouldUseIconButton) {
      return (
        <IconButton
          variant="outline"
          size="medium"
          disabled={disable}
          onClick={toggleView}
          aria-label={label}
        >
          {icon}
        </IconButton>
      );
    }

    return (
      <Button
        variant="outline"
        size="medium"
        disabled={disable}
        onClick={toggleView}
        withIconStart={icon}
      >
        {label}
      </Button>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      className={hasCustomStyles ? styles.whiteLabeledToggle : undefined}
    >
      <Toggle
        id="accessible-view"
        checked={isAccessibleView}
        disabled={disable}
        onChange={toggleView}
      />
      <Box marginLeft="Micro Small">
        <FormLabel variant="control" htmlFor="accessible-view">
          <Text
            size={displaySmall ? 'medium' : 'small'}
            color={hasCustomStyles ? 'subtle' : 'standard'}
          >
            {intl.formatMessage(messages.mobile)}
          </Text>
        </FormLabel>
      </Box>
    </Box>
  );
}
